import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  TextField,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { IssuePlannerCartItem, IssuePlannerItem, IssuePlannerSizeOption } from './types';
import { NON_SIZED_OPTION_ID } from './types';

/** A location option displayed in the per-item location dropdown. */
export interface LocationOption {
  id: string;
  label: string;
  /** Grouping label shown as a subheader (e.g. "Primary", "Secondary", "Related"). */
  group?: string;
  /** On-hand (physical) quantity at this location — used for the display label only. */
  qty?: number;
  /** Available (unreserved) quantity at this location — used to cap cart additions. */
  availableQty?: number;
  /** If true, the option is shown but not selectable. */
  disabled?: boolean;
}

interface StatusDisplay {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  isChip: boolean;
}

export interface IssuePlannerTableProps {
  customerId?: string;
  isLoading: boolean;
  plannerItems: IssuePlannerItem[];
  ieCartMap: Map<string, IssuePlannerCartItem>;
  plannedRequestCounts: Record<string, number>;
  plannerActionId: string | null;
  isBulkProcessing: boolean;
  readOnly?: boolean;
  sizeOptionsByItemId: Map<string, IssuePlannerSizeOption[]>;
  sizeSelections: Record<string, string | undefined>;
  isSizeSelectionsLoading: boolean;
  sizeAvailabilityByCatalogId: Record<string, number | undefined>;
  onSizeChange: (itemId: string, catalogId: string) => void;
  onQuantityChange: (itemId: string, quantity: number) => void;
  onAddPlannerItem: (plannerItem: IssuePlannerItem) => void;
  onRemovePlannerItem: (itemId: string) => void;
  /** Per-item location options (grouped: Primary, Secondary, Related). */
  locationOptionsByItemId: Map<string, LocationOption[]>;
  /** Currently selected location ID per item (defaults to primary). */
  selectedLocationByItemId: Record<string, string | undefined>;
  /** Called when user picks a location for an item. */
  onLocationChange: (itemId: string, locationId: string) => void;
  /** Whether location data is still loading. */
  locationsLoading?: boolean;
  /** If true, defaults location to the selectable option with the highest available quantity. */
  preferBestQuantityLocationDefault?: boolean;
}

const getSelectableDefaultLocationId = (
  options: LocationOption[],
  preferBestQuantityLocationDefault: boolean,
): string => {
  const selectableOptions = options.filter((o) => !o.disabled);
  if (selectableOptions.length === 0) return '';
  if (!preferBestQuantityLocationDefault) {
    return selectableOptions[0]?.id ?? '';
  }

  let best = selectableOptions[0]!;
  let bestQty = best.availableQty ?? best.qty ?? 0;
  for (const option of selectableOptions.slice(1)) {
    const qty = option.availableQty ?? option.qty ?? 0;
    if (qty > bestQty) {
      best = option;
      bestQty = qty;
    }
  }
  return best.id;
};

const getStatusDisplay = (
  plannerItem: IssuePlannerItem,
  ieCartMap: Map<string, IssuePlannerCartItem>,
  plannedRequestQty: number,
  isOutOfStock?: boolean,
): StatusDisplay => {
  const cartQty = ieCartMap.get(plannerItem.id)?.assetIds.length ?? 0;
  const plannedQty = Math.max(0, plannedRequestQty);
  const remainingNeed = plannerItem.need - cartQty;
  const remainingAfterPlanned = plannerItem.need - cartQty - plannedQty;

  if (plannerItem.need <= 0) {
    return { label: 'Fully assigned', color: 'success', isChip: true };
  }

  if (cartQty === 0 && plannedQty > 0) {
    return { label: `Planned ${plannedQty}`, color: 'info', isChip: true };
  }

  if (plannedQty > 0 && remainingAfterPlanned <= 0) {
    return { label: `Planned ${plannedQty}`, color: 'info', isChip: true };
  }

  if (plannedQty > 0 && remainingAfterPlanned > 0) {
    return {
      label: `Planned ${plannedQty} • Need ${remainingAfterPlanned} more`,
      color: 'warning',
      isChip: true,
    };
  }

  if (cartQty === 0 && isOutOfStock) {
    return { label: 'Out of stock', color: 'default', isChip: true };
  }

  if (cartQty === 0) {
    return { label: '✖ Not added', color: 'default', isChip: true };
  }

  if (plannerItem.need > 0 && cartQty < plannerItem.need) {
    return { label: `✖ Need ${remainingNeed} more`, color: 'warning', isChip: true };
  }

  return { label: `✔ Qty ${cartQty} in cart`, color: 'success', isChip: true };
};

export const IssuePlannerTable = ({
  customerId,
  isLoading,
  plannerItems,
  ieCartMap,
  plannedRequestCounts,
  plannerActionId,
  isBulkProcessing,
  readOnly = false,
  sizeOptionsByItemId,
  sizeSelections,
  isSizeSelectionsLoading,
  sizeAvailabilityByCatalogId,
  onSizeChange,
  onQuantityChange,
  onAddPlannerItem,
  onRemovePlannerItem,
  locationOptionsByItemId,
  selectedLocationByItemId,
  onLocationChange,
  locationsLoading = false,
  preferBestQuantityLocationDefault = false,
}: IssuePlannerTableProps) => {
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const getSizeOptionLabel = (option: IssuePlannerSizeOption) => {
    if (option.catalogId === NON_SIZED_OPTION_ID) {
      return option.displayName;
    }
    const rawLabel = option.sizeLabel ?? option.displayName;
    // Guard against doubled size values like "XL/XL" → "XL" (WI #29870)
    const dedupMatch = rawLabel.match(/^([^/]+)\/\1$/);
    const baseLabel = dedupMatch ? dedupMatch[1] : rawLabel;
    const availability = sizeAvailabilityByCatalogId[option.catalogId];
    if (availability === undefined) {
      return `${baseLabel} • Availability unknown`;
    }
    return availability > 0
      ? `${baseLabel} • ${availability} available`
      : `${baseLabel} • Unavailable`;
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: '70vh' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
          <Table size="small" aria-label="Issue planner items">
            <TableHead>
              <TableRow>
                <TableCell>{ieKitLabel}</TableCell>
                <TableCell>LIN</TableCell>
                <TableCell>NSN</TableCell>
                <TableCell>Nomenclature</TableCell>
                <TableCell align="right">{ieKitLabel} Qty</TableCell>
                <TableCell align="right">HR Qty</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Location</TableCell>
                <TableCell align="right">Issue Qty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plannerItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {customerId
                        ? `No ${ieKitLabel} items found for this customer`
                        : 'No customer selected'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                plannerItems.map((row) => {
                  const sizeOptions = sizeOptionsByItemId.get(row.id);
                  const requiresSizeSelection = Boolean(sizeOptions && sizeOptions.length > 0);
                  const selectableLocationOptions = (
                    locationOptionsByItemId.get(row.id) ?? []
                  ).filter((o) => !o.disabled);
                  const defaultLocId = getSelectableDefaultLocationId(
                    selectableLocationOptions,
                    preferBestQuantityLocationDefault,
                  );
                  const selectedLocIdForAvailability =
                    selectedLocationByItemId[row.id] ?? defaultLocId;
                  const selectedLocationOption = selectableLocationOptions.find(
                    (o) => o.id === selectedLocIdForAvailability,
                  );
                  const selectedLocationAvailableQty =
                    selectedLocationOption?.availableQty ?? selectedLocationOption?.qty ?? 0;
                  const hasAnyAvailableLocation = selectableLocationOptions.some((option) => {
                    const capQty = option.availableQty ?? option.qty ?? 0;
                    return capQty > 0;
                  });
                  const allSizesUnavailable = Boolean(
                    requiresSizeSelection &&
                    (sizeOptions?.filter((option) => option.catalogId !== NON_SIZED_OPTION_ID)
                      .length ?? 0) > 0 &&
                    sizeOptions
                      ?.filter((option) => option.catalogId !== NON_SIZED_OPTION_ID)
                      .every((option) => {
                        const availability = sizeAvailabilityByCatalogId[option.catalogId];
                        return typeof availability === 'number' && availability <= 0;
                      }),
                  );
                  const isOutOfStockForStatus =
                    allSizesUnavailable ||
                    (!requiresSizeSelection && !hasAnyAvailableLocation && row.need > 0);
                  const statusDisplay = getStatusDisplay(
                    row,
                    ieCartMap,
                    plannedRequestCounts[row.id] ?? 0,
                    isOutOfStockForStatus,
                  );
                  const kitDisplay =
                    row.kitName && row.kitName.trim().length > 0 ? row.kitName : '—';
                  const cartQty = ieCartMap.get(row.id)?.assetIds.length ?? 0;
                  const hasInCart = cartQty > 0;
                  const remainingNeed = Math.max(0, row.need - cartQty);
                  const isRowProcessing = plannerActionId === row.id;
                  const isFullyAssigned = remainingNeed <= 0;
                  const isRowDisabled = isRowProcessing || isBulkProcessing || isFullyAssigned;
                  const iconCursor = isRowDisabled ? 'default' : 'pointer';
                  const selectedSize = sizeSelections[row.id] ?? '';
                  const isSizePending = isSizeSelectionsLoading && Boolean(row.catalogId);
                  const selectedCatalogId =
                    selectedSize && selectedSize.trim().length > 0 ? selectedSize : null;
                  const isNonSizedSelection = selectedCatalogId === NON_SIZED_OPTION_ID;
                  const selectedCatalogAvailability = selectedCatalogId
                    ? sizeAvailabilityByCatalogId[selectedCatalogId]
                    : undefined;
                  const isSelectedSizeOutOfStock =
                    !isNonSizedSelection &&
                    typeof selectedCatalogAvailability === 'number' &&
                    selectedCatalogAvailability <= 0;
                  const isNonSizedOutOfStock =
                    !requiresSizeSelection &&
                    selectableLocationOptions.length > 0 &&
                    selectedLocationAvailableQty <= 0 &&
                    row.need > 0;
                  const hasNoLocation =
                    !isNonSizedSelection && selectableLocationOptions.length === 0;
                  // Item has no effective catalog ID — cannot be issued regardless of other state.
                  const hasNoCatalogId = !row.catalogId?.trim();
                  const selectedLocationOutOfStock = (() => {
                    if (isNonSizedSelection) return false;
                    if (selectableLocationOptions.length === 0) return false;
                    const selectedOpt = selectableLocationOptions.find(
                      (o) => o.id === selectedLocIdForAvailability,
                    );
                    if (selectedOpt == null) return false;
                    // Use availableQty (unreserved) to gate adds; fall back to qty if not set
                    const capQty = selectedOpt.availableQty ?? selectedOpt.qty ?? 0;
                    return capQty === 0;
                  })();
                  const addButtonDisabled =
                    isRowDisabled ||
                    isSizePending ||
                    allSizesUnavailable ||
                    hasNoCatalogId ||
                    (requiresSizeSelection && !selectedCatalogId) ||
                    isSelectedSizeOutOfStock ||
                    isNonSizedOutOfStock ||
                    hasNoLocation ||
                    selectedLocationOutOfStock;
                  // Cause-specific tooltip shown only when the button is disabled (#472).
                  const addButtonTooltip: string = (() => {
                    if (!addButtonDisabled) return '';
                    if (requiresSizeSelection && !selectedCatalogId && !allSizesUnavailable)
                      return 'Please select a size first.';
                    if (hasNoCatalogId) return 'No catalog is configured for this item.';
                    if (hasNoLocation) return 'No issue location is configured for this item.';
                    return '';
                  })();
                  const addButtonCursor = addButtonDisabled ? 'default' : 'pointer';
                  const sizeControlDisabled =
                    isRowProcessing || isBulkProcessing || allSizesUnavailable || isFullyAssigned;
                  const isOutOfStock =
                    !isFullyAssigned &&
                    (allSizesUnavailable ||
                      (!requiresSizeSelection && !hasAnyAvailableLocation && row.need > 0));

                  return (
                    <TableRow
                      key={row.id}
                      hover={!isOutOfStock}
                      sx={{ opacity: isOutOfStock ? 0.5 : 1 }}
                    >
                      <TableCell>
                        {kitDisplay !== '—' ? (
                          kitDisplay
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {kitDisplay}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.lin}</TableCell>
                      <TableCell>{row.nsn}</TableCell>
                      <TableCell>{row.item}</TableCell>
                      <TableCell align="right">{row.authorizedQty}</TableCell>
                      <TableCell align="right">{row.currentQty}</TableCell>
                      <TableCell sx={{ minWidth: 160 }}>
                        {isSizePending ? (
                          <Skeleton
                            variant="rounded"
                            height={40}
                            width="100%"
                            animation="wave"
                            sx={{ my: 0.5 }}
                          />
                        ) : requiresSizeSelection && sizeOptions ? (
                          <TextField
                            select
                            size="small"
                            fullWidth
                            value={selectedSize}
                            onChange={(event) => {
                              onSizeChange(row.id, event.target.value);
                            }}
                            disabled={sizeControlDisabled || readOnly}
                            sx={{ maxWidth: 200 }}
                            SelectProps={{
                              displayEmpty: true,
                              renderValue: (value) => {
                                if (
                                  !value ||
                                  typeof value !== 'string' ||
                                  value.trim().length === 0
                                ) {
                                  return allSizesUnavailable ? 'No sizes available' : 'Select size';
                                }
                                const option = sizeOptions.find(
                                  (entry) => entry.catalogId === value,
                                );
                                return option ? getSizeOptionLabel(option) : value;
                              },
                            }}
                          >
                            <MenuItem value="" disabled>
                              {allSizesUnavailable ? 'No sizes available' : 'Select size'}
                            </MenuItem>
                            {sizeOptions.map((option) => (
                              <MenuItem key={option.catalogId} value={option.catalogId}>
                                {getSizeOptionLabel(option)}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : isNonSizedOutOfStock ? (
                          <Typography variant="body2" color="error">
                            Unavailable
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140 }}>
                        {(() => {
                          const options = locationOptionsByItemId.get(row.id) ?? [];
                          const selectableOptions = options.filter((o) => !o.disabled);
                          const defaultLocId = getSelectableDefaultLocationId(
                            selectableOptions,
                            preferBestQuantityLocationDefault,
                          );
                          const selectedLocId = selectedLocationByItemId[row.id] ?? defaultLocId;

                          if (options.length === 0) {
                            return locationsLoading ? (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                Loading…
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="error" noWrap>
                                No location
                              </Typography>
                            );
                          }

                          // Only disabled options (unlocated assets) — show warning
                          if (selectableOptions.length === 0) {
                            const unlocatedOpt = options.find((o) => o.disabled);
                            const unlocatedLabel = unlocatedOpt
                              ? `${unlocatedOpt.label} (${unlocatedOpt.qty})`
                              : 'Unknown Location';
                            return (
                              <Typography
                                variant="caption"
                                color="warning.main"
                                noWrap
                                title="Assets exist but have no assigned location"
                              >
                                {unlocatedLabel}
                              </Typography>
                            );
                          }

                          if (selectableOptions.length === 1 && options.length === 1) {
                            const opt = selectableOptions[0]!;
                            const displayLabel =
                              opt.qty != null ? `${opt.label} (${opt.qty})` : opt.label;
                            return (
                              <Typography variant="caption" noWrap title={displayLabel}>
                                {displayLabel}
                              </Typography>
                            );
                          }

                          return (
                            <Select
                              size="small"
                              value={selectedLocId}
                              onChange={(event) => {
                                onLocationChange(row.id, event.target.value as string);
                              }}
                              disabled={isRowProcessing || isBulkProcessing || readOnly}
                              variant="standard"
                              disableUnderline
                              sx={{ fontSize: '0.75rem', maxWidth: 130 }}
                            >
                              {options.map((opt, idx) => {
                                const prevGroup = idx > 0 ? options[idx - 1]?.group : undefined;
                                const showHeader = opt.group && opt.group !== prevGroup;
                                const optLabel =
                                  opt.qty != null ? `${opt.label} (${opt.qty})` : opt.label;
                                return [
                                  showHeader && (
                                    <ListSubheader
                                      key={`header-${opt.group}`}
                                      sx={{ lineHeight: '24px', fontSize: '0.7rem' }}
                                    >
                                      {opt.group}
                                    </ListSubheader>
                                  ),
                                  <MenuItem
                                    key={opt.id}
                                    value={opt.id}
                                    disabled={opt.disabled}
                                    sx={{
                                      fontSize: '0.75rem',
                                      minHeight: 28,
                                      ...(opt.disabled
                                        ? { opacity: 0.6, fontStyle: 'italic' }
                                        : {}),
                                    }}
                                  >
                                    {optLabel}
                                  </MenuItem>,
                                ];
                              })}
                            </Select>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right" sx={{ minWidth: 100 }}>
                        {(() => {
                          // Cap issue qty at available (unreserved) stock at the selected location
                          const locOptions = (locationOptionsByItemId.get(row.id) ?? []).filter(
                            (o) => !o.disabled,
                          );
                          const defaultLocId = getSelectableDefaultLocationId(
                            locOptions,
                            preferBestQuantityLocationDefault,
                          );
                          const selectedLocId = selectedLocationByItemId[row.id] ?? defaultLocId;
                          const selectedLocOpt = locOptions.find((o) => o.id === selectedLocId);
                          const availableAtLoc =
                            selectedLocOpt != null
                              ? (selectedLocOpt.availableQty ?? selectedLocOpt.qty ?? 0)
                              : row.availableQty;
                          const maxQty = Math.min(row.need, availableAtLoc);
                          return (
                            <TextField
                              type="number"
                              size="small"
                              value={row.quantityToIssue ?? remainingNeed}
                              onChange={(e) => {
                                const val = Math.max(
                                  0,
                                  Math.min(maxQty, parseInt(e.target.value) || 0),
                                );
                                onQuantityChange(row.id, val);
                              }}
                              disabled={isRowDisabled || isOutOfStock || readOnly}
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  max: maxQty,
                                  style: { textAlign: 'right' },
                                },
                              }}
                              sx={{ width: '80px' }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {statusDisplay.isChip ? (
                          <Chip
                            size="small"
                            label={statusDisplay.label}
                            color={statusDisplay.color}
                            sx={{ fontWeight: 500 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {statusDisplay.label}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {hasInCart ? (
                            <Tooltip title="Remove from cart">
                              <Box
                                component="span"
                                sx={{ display: 'inline-flex', cursor: iconCursor }}
                              >
                                <IconButton
                                  size="small"
                                  color="primary"
                                  aria-label="Remove from cart"
                                  disabled={isRowDisabled || readOnly}
                                  sx={{ cursor: iconCursor }}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onRemovePlannerItem(row.id);
                                  }}
                                >
                                  <RemoveIcon fontSize="inherit" />
                                </IconButton>
                              </Box>
                            </Tooltip>
                          ) : null}
                          <Tooltip
                            title={addButtonTooltip}
                            disableHoverListener={!addButtonTooltip}
                            disableFocusListener={!addButtonTooltip}
                          >
                            <Box
                              component="span"
                              sx={{ display: 'inline-flex', cursor: addButtonCursor }}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={hasInCart ? 'Add more to cart' : 'Add to cart'}
                                disabled={addButtonDisabled || readOnly}
                                sx={{ cursor: addButtonCursor }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onAddPlannerItem(row);
                                }}
                              >
                                {isRowProcessing ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <AddIcon fontSize="inherit" />
                                )}
                              </IconButton>
                            </Box>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Paper>
  );
};
